/**
 * A minimal QR encoder (byte mode, error-correction level M, versions 1–10).
 *
 * WHY THIS IS HAND-WRITTEN RATHER THAN A SERVICE
 *
 * A deposit address is the one thing on the page that must not be sent anywhere.
 * Rendering the QR through an image API would leak every deposit address to a third
 * party, so the QR is generated locally. A dependency would also be the only
 * runtime dependency this project has beyond React — for ~200 lines.
 *
 * Scope is deliberately narrow: byte mode (addresses and `tron:`/`ethereum:` URIs
 * are ASCII), level M, versions 1–10 (up to 216 bytes). Anything larger returns
 * null and the caller falls back to showing the address text, which is why every
 * caller treats a null result as "show the text instead", never as an error.
 *
 * The implementation follows ISO/IEC 18004: data encoding, Reed–Solomon error
 * correction, block interleaving, module placement and the eight mask patterns with
 * their penalty rules.
 */

/** Error-correction codewords per block and block layout, index = version. */
interface EcInfo {
  /** Total codewords in the symbol. */
  totalCodewords: number;
  /** EC codewords per block. */
  ecCodewordsPerBlock: number;
  /** Number of blocks in group 1 / group 2. */
  group1Blocks: number;
  group1DataCodewords: number;
  group2Blocks: number;
  group2DataCodewords: number;
}

// Level M only. Version 1..10.
const EC_M: readonly EcInfo[] = [
  { totalCodewords: 26, ecCodewordsPerBlock: 10, group1Blocks: 1, group1DataCodewords: 16, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 44, ecCodewordsPerBlock: 16, group1Blocks: 1, group1DataCodewords: 28, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 70, ecCodewordsPerBlock: 26, group1Blocks: 1, group1DataCodewords: 44, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 100, ecCodewordsPerBlock: 18, group1Blocks: 2, group1DataCodewords: 32, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 134, ecCodewordsPerBlock: 24, group1Blocks: 2, group1DataCodewords: 43, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 172, ecCodewordsPerBlock: 16, group1Blocks: 4, group1DataCodewords: 27, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 196, ecCodewordsPerBlock: 18, group1Blocks: 4, group1DataCodewords: 31, group2Blocks: 0, group2DataCodewords: 0 },
  { totalCodewords: 242, ecCodewordsPerBlock: 22, group1Blocks: 2, group1DataCodewords: 38, group2Blocks: 2, group2DataCodewords: 39 },
  { totalCodewords: 292, ecCodewordsPerBlock: 22, group1Blocks: 3, group1DataCodewords: 36, group2Blocks: 2, group2DataCodewords: 37 },
  { totalCodewords: 346, ecCodewordsPerBlock: 26, group1Blocks: 4, group1DataCodewords: 43, group2Blocks: 1, group2DataCodewords: 44 },
];

/** Length of the character-count indicator for byte mode, per version group. */
function lengthBits(version: number): number {
  if (version <= 9) return 8;
  return 16;
}

/** Total data codewords available at this version/level. */
function dataCodewordCount(info: EcInfo): number {
  return (
    info.group1Blocks * info.group1DataCodewords +
    info.group2Blocks * info.group2DataCodewords
  );
}

// --- Galois field arithmetic for Reed–Solomon ------------------------------

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

(function initGaloisField(): void {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d; // primitive polynomial for QR
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
})();

function gfMultiply(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a] + LOG[b]];
}

/** Generator polynomial for `degree` error-correction codewords. */
function generatorPolynomial(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMultiply(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

/** Reed–Solomon remainder for one block. */
function ecCodewords(data: number[], degree: number): number[] {
  const gen = generatorPolynomial(degree);
  const remainder = new Array(degree).fill(0);

  for (const byte of data) {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    for (let i = 0; i < degree; i += 1) {
      remainder[i] ^= gfMultiply(gen[i + 1], factor);
    }
  }
  return remainder;
}

// --- Bit buffer -------------------------------------------------------------

class BitBuffer {
  readonly bits: number[] = [];

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) {
      this.bits.push((value >>> i) & 1);
    }
  }
}

// --- Matrix construction ----------------------------------------------------

type Matrix = (boolean | null)[][];

function createMatrix(size: number): Matrix {
  return Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null));
}

/** Alignment-pattern centre coordinates per version. */
const ALIGNMENT_CENTRES: readonly number[][] = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

/** Set a module, tracking which cells are function patterns (never masked). */
function setModule(
  matrix: Matrix,
  reserved: boolean[][],
  x: number,
  y: number,
  dark: boolean,
): void {
  if (y < 0 || y >= matrix.length || x < 0 || x >= matrix.length) return;
  matrix[y][x] = dark;
  reserved[y][x] = true;
}

function drawFinder(matrix: Matrix, reserved: boolean[][], cx: number, cy: number): void {
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      const dark = distance !== 2 && distance !== 4;
      setModule(matrix, reserved, cx + dx, cy + dy, distance <= 4 ? dark : false);
    }
  }
}

function drawAlignment(matrix: Matrix, reserved: boolean[][], cx: number, cy: number): void {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      setModule(matrix, reserved, cx + dx, cy + dy, distance !== 1);
    }
  }
}

/**
 * BCH(15,5) / BCH(18,6) format and version information.
 *
 * The generator polynomials are the ones the specification defines; the same
 * routine produces the format bits used by the mask evaluation.
 */
function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask; // EC level M == 0b00
  let value = data << 10;
  const generator = 0b10100110111;
  for (let i = 14; i >= 10; i -= 1) {
    if ((value >>> i) & 1) value ^= generator << (i - 10);
  }
  return ((data << 10) | value) ^ 0b101010000010010;
}

function versionBits(version: number): number {
  let value = version << 12;
  const generator = 0b1111100100101;
  for (let i = 17; i >= 12; i -= 1) {
    if ((value >>> i) & 1) value ^= generator << (i - 12);
  }
  return (version << 12) | value;
}

function drawFunctionPatterns(matrix: Matrix, reserved: boolean[][], version: number): void {
  const size = matrix.length;

  drawFinder(matrix, reserved, 3, 3);
  drawFinder(matrix, reserved, size - 4, 3);
  drawFinder(matrix, reserved, 3, size - 4);

  for (let i = 0; i < size; i += 1) {
    // Timing patterns.
    if (!reserved[6][i]) setModule(matrix, reserved, i, 6, i % 2 === 0);
    if (!reserved[i][6]) setModule(matrix, reserved, 6, i, i % 2 === 0);
  }

  const centres = ALIGNMENT_CENTRES[version] ?? [];
  for (const cy of centres) {
    for (const cx of centres) {
      // Skip the three corners, which the finder patterns already occupy.
      const nearFinder =
        (cx <= 8 && cy <= 8) ||
        (cx >= size - 9 && cy <= 8) ||
        (cx <= 8 && cy >= size - 9);
      if (!nearFinder) drawAlignment(matrix, reserved, cx, cy);
    }
  }

  // Reserve the format-information areas (values are written after masking).
  for (let i = 0; i < 9; i += 1) {
    if (i !== 6) {
      reserved[8][i] = true;
      reserved[i][8] = true;
    }
  }
  for (let i = 0; i < 8; i += 1) {
    reserved[8][size - 1 - i] = true;
    reserved[size - 1 - i][8] = true;
  }
  setModule(matrix, reserved, 8, size - 8, true); // dark module

  if (version >= 7) {
    for (let i = 0; i < 6; i += 1) {
      for (let j = 0; j < 3; j += 1) {
        reserved[size - 11 + j][i] = true;
        reserved[i][size - 11 + j] = true;
      }
    }
  }
}

function placeData(matrix: Matrix, reserved: boolean[][], codewords: number[]): void {
  const size = matrix.length;
  let bitIndex = 0;
  const totalBits = codewords.length * 8;

  const nextBit = (): boolean => {
    if (bitIndex >= totalBits) return false;
    const byte = codewords[bitIndex >> 3];
    const bit = (byte >>> (7 - (bitIndex & 7))) & 1;
    bitIndex += 1;
    return bit === 1;
  };

  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern and is skipped.
    const column = right <= 6 ? right - 1 : right;
    for (let row = 0; row < size; row += 1) {
      const upward = ((right + 1) & 2) === 0;
      const y = upward ? size - 1 - row : row;
      for (let c = 0; c < 2; c += 1) {
        const x = column - c;
        if (reserved[y][x]) continue;
        matrix[y][x] = nextBit();
      }
    }
  }
}

function applyMask(matrix: Matrix, reserved: boolean[][], mask: number): void {
  const size = matrix.length;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (reserved[y][x]) continue;
      let invert = false;
      switch (mask) {
        case 0: invert = (x + y) % 2 === 0; break;
        case 1: invert = y % 2 === 0; break;
        case 2: invert = x % 3 === 0; break;
        case 3: invert = (x + y) % 3 === 0; break;
        case 4: invert = (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0; break;
        case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
        case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
      }
      if (invert) matrix[y][x] = !matrix[y][x];
    }
  }
}


function writeFormatInfo(matrix: Matrix, mask: number): void {
  const size = matrix.length;
  const bits = formatBits(mask);

  for (let i = 0; i < 15; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;

    // Copy 1: around the top-left finder.
    if (i < 6) matrix[i][8] = dark;
    else if (i === 6) matrix[7][8] = dark;
    else if (i === 7) matrix[8][8] = dark;
    else if (i === 8) matrix[8][7] = dark;
    else matrix[8][14 - i] = dark;

    // Copy 2: split between the other two finders.
    if (i < 8) matrix[8][size - 1 - i] = dark;
    else matrix[size - 15 + i][8] = dark;
  }
}

function writeVersionInfo(matrix: Matrix, version: number): void {
  if (version < 7) return;
  const size = matrix.length;
  const bits = versionBits(version);

  for (let i = 0; i < 18; i += 1) {
    const dark = ((bits >>> i) & 1) === 1;
    const a = Math.floor(i / 3);
    const b = (i % 3) + size - 11;
    matrix[b][a] = dark;
    matrix[a][b] = dark;
  }
}

/**
 * Mask penalty score (rules 1–4 of the specification).
 *
 * Lower is better. The encoder tries all eight masks and keeps the best, which is
 * what keeps the symbol readable for a phone camera in poor light.
 */
function penalty(matrix: boolean[][]): number {
  const size = matrix.length;
  let score = 0;

  // Rule 1: runs of five or more same-coloured modules, by row and by column.
  for (let y = 0; y < size; y += 1) {
    let run = 1;
    for (let x = 1; x < size; x += 1) {
      if (matrix[y][x] === matrix[y][x - 1]) {
        run += 1;
        if (run === 5) score += 3;
        else if (run > 5) score += 1;
      } else run = 1;
    }
  }
  for (let x = 0; x < size; x += 1) {
    let run = 1;
    for (let y = 1; y < size; y += 1) {
      if (matrix[y][x] === matrix[y - 1][x]) {
        run += 1;
        if (run === 5) score += 3;
        else if (run > 5) score += 1;
      } else run = 1;
    }
  }

  // Rule 2: 2x2 blocks of one colour.
  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const value = matrix[y][x];
      if (
        value === matrix[y][x + 1] &&
        value === matrix[y + 1][x] &&
        value === matrix[y + 1][x + 1]
      ) {
        score += 3;
      }
    }
  }

  // Rule 3: finder-like 1:1:3:1:1 patterns in rows and columns.
  const pattern = [true, false, true, true, true, false, true];
  const matchesAt = (get: (k: number) => boolean): boolean => {
    for (let k = 0; k < 7; k += 1) if (get(k) !== pattern[k]) return false;
    return true;
  };
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x + 6 < size; x += 1) {
      if (matchesAt((k) => matrix[y][x + k])) score += 40;
    }
  }
  for (let x = 0; x < size; x += 1) {
    for (let y = 0; y + 6 < size; y += 1) {
      if (matchesAt((k) => matrix[y + k][x])) score += 40;
    }
  }

  // Rule 4: deviation from a 50% dark ratio.
  let darkModules = 0;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) if (matrix[y][x]) darkModules += 1;
  }
  const ratio = (darkModules * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;

  return score;
}


export interface QrCode {
  /** Number of modules per side. */
  size: number;
  /** `modules[y][x]`, true = dark. */
  modules: boolean[][];
}

/**
 * Encode text as a QR symbol.
 *
 * Returns null when the input does not fit the supported range (byte mode, level M,
 * versions 1–10). Callers MUST treat null as "render the address as text instead"
 * rather than as an error, so an unexpectedly long value degrades to a usable page
 * instead of a broken one.
 */
export function encodeQr(text: string): QrCode | null {
  const bytes = Array.from(new TextEncoder().encode(text));

  let version = 0;
  let info: EcInfo | null = null;
  for (let candidate = 1; candidate <= 10; candidate += 1) {
    const candidateInfo = EC_M[candidate - 1];
    const neededBits = 4 + lengthBits(candidate) + bytes.length * 8;
    if (neededBits <= dataCodewordCount(candidateInfo) * 8) {
      version = candidate;
      info = candidateInfo;
      break;
    }
  }
  if (!info) return null;

  // --- Data codewords ---
  const buffer = new BitBuffer();
  buffer.put(0b0100, 4); // byte mode
  buffer.put(bytes.length, lengthBits(version));
  for (const byte of bytes) buffer.put(byte, 8);

  const capacityBits = dataCodewordCount(info) * 8;
  buffer.put(0, Math.min(4, capacityBits - buffer.bits.length)); // terminator
  while (buffer.bits.length % 8 !== 0) buffer.bits.push(0);

  const dataCodewords: number[] = [];
  for (let i = 0; i < buffer.bits.length; i += 8) {
    let byte = 0;
    for (let bit = 0; bit < 8; bit += 1) byte = (byte << 1) | buffer.bits[i + bit];
    dataCodewords.push(byte);
  }
  const padBytes = [0xec, 0x11];
  let padIndex = 0;
  while (dataCodewords.length < dataCodewordCount(info)) {
    dataCodewords.push(padBytes[padIndex % 2]);
    padIndex += 1;
  }

  // --- Split into blocks, compute one EC block per data block ---
  const blocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (const group of [
    { count: info.group1Blocks, size: info.group1DataCodewords },
    { count: info.group2Blocks, size: info.group2DataCodewords },
  ]) {
    for (let b = 0; b < group.count; b += 1) {
      const block = dataCodewords.slice(offset, offset + group.size);
      offset += group.size;
      blocks.push(block);
      ecBlocks.push(ecCodewords(block, info.ecCodewordsPerBlock));
    }
  }

  // --- Interleave data codewords, then EC codewords ---
  const maxData = Math.max(...blocks.map((block) => block.length));
  const interleaved: number[] = [];
  for (let i = 0; i < maxData; i += 1) {
    for (const block of blocks) if (i < block.length) interleaved.push(block[i]);
  }
  for (let i = 0; i < info.ecCodewordsPerBlock; i += 1) {
    for (const block of ecBlocks) interleaved.push(block[i]);
  }

  // --- Build the matrix for every mask and keep the lowest-penalty result ---
  const size = version * 4 + 17;
  let best: { modules: boolean[][]; score: number } | null = null;

  for (let mask = 0; mask < 8; mask += 1) {
    const matrix = createMatrix(size);
    const reserved = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

    drawFunctionPatterns(matrix, reserved, version);
    placeData(matrix, reserved, interleaved);
    applyMask(matrix, reserved, mask);
    writeFormatInfo(matrix, mask);
    writeVersionInfo(matrix, version);

    const modules = matrix.map((row) => row.map((cell) => cell === true));
    const score = penalty(modules);
    if (!best || score < best.score) best = { modules, score };
  }

  return best ? { size, modules: best.modules } : null;
}

/** An SVG path for the dark modules, so the symbol renders as one `<path>`. */
export function qrToSvgPath(code: QrCode): string {
  let path = '';
  for (let y = 0; y < code.size; y += 1) {
    for (let x = 0; x < code.size; x += 1) {
      if (code.modules[y][x]) path += `M${x} ${y}h1v1h-1z`;
    }
  }
  return path;
}

